<script setup>
/**
 * Chart.js, wrapped once and styled to the house rules so every chart in OKANE
 * reads the same: thin marks, a hairline grid, and a tooltip in the app's own
 * chrome rather than the canvas default.
 *
 * One series gets a direct label at its latest point and no legend — the card's
 * title names it. Two or more get a legend and leave the numbers to the axis
 * and the tooltip, which always lists every series at the hovered position.
 *
 * Canvas is opaque to a screen reader, so the values are also emitted as a
 * visually-hidden table. Privacy mode never hides the shape, only the figures:
 * the y axis goes away and every number is drawn through `format`, which is the
 * app's masking formatter.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  DoughnutController,
  Filler,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js'
import { state } from '../../stores/state'
import { niceScale } from '../../lib/scale'
import { t } from '../../stores/i18n'

Chart.register(
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  DoughnutController,
  Filler,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
)

const props = defineProps({
  /**
   * bar | line | pie | segments. A `pie` and a `segments` take one series per
   * slice, each holding a single value — the same split drawn round a circle or
   * laid along one line.
   */
  type: { type: String, default: 'bar' },
  labels: { type: Array, default: () => [] },
  /** [{ label, values, color, type?, dash? }] — `color` is a CSS variable name. */
  series: { type: Array, default: () => [] },
  format: { type: Function, default: (v) => String(v) },
  /** Axis ticks are tighter for space; defaults to `format`. */
  tickFormat: { type: Function, default: null },
  masked: { type: Boolean, default: false },
  caption: { type: String, default: '' },
  height: { type: Number, default: 132 },
  /** Calendar boundaries: `[{ index, label }]`, drawn just before that column. */
  marks: { type: Array, default: () => [] },
  /** Lets a pointer drag out a span of columns; emits `select` with the ends. */
  selectable: { type: Boolean, default: false },
  /** `{ value, label }` for the hole of a doughnut, which is otherwise wasted. */
  centre: { type: Object, default: null },
})

const emit = defineEmits(['select'])

const canvas = ref(null)
const tip = ref(null)
const drag = ref(null)
let chart = null

const css = (name) => getComputedStyle(document.documentElement).getPropertyValue(name).trim()

function rgba(hex, alpha) {
  const full = hex.length === 4 ? hex.replace(/[^#]/g, (c) => c + c) : hex
  const n = parseInt(full.slice(1), 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

const single = computed(() => props.series.length === 1)

/**
 * A legend as soon as identity is not obvious: two or more series, or a single
 * one that is not the card's own subject — a tag on its own needs naming, the
 * base line does not.
 */
const showLegend = computed(
  () => props.series.length > 1 || (props.series.length === 1 && props.series[0].fill !== true),
)
const isPie = computed(() => props.type === 'pie')
const isSegments = computed(() => props.type === 'segments')
const isSplit = computed(() => isPie.value || isSegments.value)
const isLine = (series) => (series.type ?? props.type) === 'line'
const values = computed(() => props.series.flatMap((s) => s.values))

/** What the slices come to, which a split is always read against. */
const splitTotal = computed(() =>
  props.series.reduce((sum, series) => sum + (series.values[0] ?? 0), 0),
)

/**
 * The axis is cut to the data: a balance that never goes negative is never
 * given a negative half, and a line that moves by a few hundred is not
 * flattened against a scale that starts at zero. Columns always keep their zero
 * baseline — a bar measured from anywhere else lies about its own length.
 */
const scale = computed(() =>
  niceScale(props.type === 'bar' ? [0, ...values.value] : values.value),
)

/**
 * The one direct label, on the base series: the latest point, or the biggest
 * when it is empty. Several lines of the same standing share the legend and the
 * tooltip instead — a label on whichever came first would just pick a favourite.
 */
const labelAt = computed(() => {
  const base = single.value || (props.type === 'line' && props.series[0]?.fill === true)
  if (!base) return -1
  const only = props.series[0]?.values ?? []
  if (!only.length) return -1
  const last = only.length - 1
  if (only[last]) return last
  const biggest = only.reduce((best, v, i) => (Math.abs(v) > Math.abs(only[best]) ? i : best), 0)
  return only[biggest] ? biggest : -1
})

/**
 * The month (or year) each column belongs to: a hairline just before the first
 * column of the run, and its name above the plot. The line is drawn under the
 * data — it is calendar, not measurement — and a name is skipped rather than
 * crowded when the boundaries come closer together than it is wide.
 */
const boundaries = {
  id: 'boundaries',
  beforeDatasetsDraw(instance, _args, options) {
    const marks = options.marks ?? []
    if (!marks.length) return
    const { ctx, chartArea: area, scales } = instance
    ctx.save()
    ctx.font = `600 10px ${getComputedStyle(instance.canvas).fontFamily}`
    ctx.textBaseline = 'top'
    ctx.textAlign = 'left'
    marks.forEach((mark, i) => {
      // Halfway between the columns, so the line separates them rather than
      // striking through the first one.
      const at = scales.x.getPixelForValue(mark.index)
      const before = scales.x.getPixelForValue(Math.max(0, mark.index - 1))
      const x = Math.round(mark.index > 0 ? (at + before) / 2 : at)
      if (x <= area.left || x >= area.right) return
      ctx.strokeStyle = options.line
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(x + 0.5, area.top)
      ctx.lineTo(x + 0.5, area.bottom)
      ctx.stroke()
      const next = marks[i + 1]
      const room = (next ? scales.x.getPixelForValue(next.index) : area.right) - x - 6
      if (room >= ctx.measureText(mark.label).width) {
        // Above the plot, in the padding the direct label already reserves, so
        // a month name never sits on top of the data it names.
        ctx.fillStyle = options.text
        ctx.fillText(mark.label, x + 4, area.top - 13)
      }
    })
    ctx.restore()
  },
}

/**
 * Names each piece of a segmented bar, inside it where it fits. A piece too
 * narrow for its own name is left to the legend and the tooltip rather than
 * being given a label that overlaps its neighbour.
 */
const segmentLabels = {
  id: 'segmentLabels',
  afterDatasetsDraw(instance, _args, options) {
    const { ctx } = instance
    ctx.save()
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    options.names.forEach((name, i) => {
      const bar = instance.getDatasetMeta(i)?.data?.[0]
      if (!bar) return
      const { x, y, base } = bar.getProps(['x', 'y', 'base'], true)
      const width = Math.abs(x - base)
      const share = `${Math.round(options.shares[i] * 100)}%`
      const text = options.masked ? share : `${name} ${share}`
      ctx.font = `600 11px ${getComputedStyle(instance.canvas).fontFamily}`
      // Only if the whole label fits inside the piece it names.
      if (ctx.measureText(text).width + 12 > width) return
      const middle = (x + base) / 2
      ctx.lineWidth = 3
      ctx.strokeStyle = options.halo
      ctx.strokeText(text, middle, y)
      ctx.fillStyle = options.color
      ctx.fillText(text, middle, y)
    })
    ctx.restore()
  },
}

/** Draws that label on the canvas — a plugin rather than another dependency. */
const directLabel = {
  id: 'directLabel',
  afterDatasetsDraw(instance, _args, options) {
    const element = instance.getDatasetMeta(0).data[options.index]
    if (!element || options.index < 0) return
    const { ctx } = instance
    ctx.save()
    ctx.font = `600 11.5px ${getComputedStyle(instance.canvas).fontFamily}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'
    // Measure before placing: a clipped label is worse than none.
    const half = ctx.measureText(options.text).width / 2
    const x = Math.min(Math.max(element.x, half + 2), instance.chartArea.right - half)
    const y = Math.max(element.y - 8, 12)
    ctx.lineWidth = 3
    ctx.strokeStyle = options.halo // so the line beneath cannot cut through it
    ctx.strokeText(options.text, x, y)
    ctx.fillStyle = options.color
    ctx.fillText(options.text, x, y)
    ctx.restore()
  },
}

function externalTooltip({ chart: instance, tooltip }) {
  // A drag is its own readout; two labels fighting over the same spot is one
  // too many.
  if (!tooltip.opacity || drag.value) {
    tip.value = null
    return
  }
  const points = tooltip.dataPoints ?? []
  // A directly labelled point already shows its value on the canvas.
  if (!points.length || (single.value && points[0].dataIndex === labelAt.value)) {
    tip.value = null
    return
  }
  // On a pie every slice is its own point of one dataset, so what identifies a
  // row is the index rather than the dataset it came from.
  const slice = (point) => (isPie.value ? props.series[point.dataIndex] : props.series[point.datasetIndex])
  tip.value = {
    label: isSplit.value ? '' : points[0].label,
    rows: points.map((point) => ({
      name: slice(point)?.label ?? '',
      value: props.format(point.raw),
      color: css(slice(point)?.color ?? '--c-text'),
    })),
    x: Math.min(Math.max(points[0].element.x, 54), instance.width - 54),
  }
}

/**
 * A pie is one dataset of many slices rather than many datasets, so it is built
 * apart from the rest. A doughnut and not a disc: the hole gives the eye a
 * baseline to compare arcs against, and leaves room to say the total.
 */
function pieConfig() {
  const hues = props.series.map((slice) => css(slice.color))
  return {
    type: 'doughnut',
    data: {
      labels: props.series.map((slice) => slice.label),
      datasets: [
        {
          data: props.series.map((slice) => slice.values[0] ?? 0),
          backgroundColor: hues,
          hoverBackgroundColor: hues.map((hue) => rgba(hue, 0.82)),
          // The surface shows between the slices, the way it does between bars.
          borderColor: css('--c-surface'),
          borderWidth: 2,
          hoverOffset: 4,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '58%',
      animation: { duration: 220 },
      layout: { padding: 2 },
      interaction: { mode: 'nearest', intersect: true },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false, external: externalTooltip },
      },
    },
  }
}

/**
 * The same split as the pie, laid along one line instead of round a circle: a
 * single stacked bar, where a length is easier to compare than an angle and
 * there is room to name the pieces.
 */
function segmentsConfig() {
  return {
    type: 'bar',
    data: {
      labels: [''],
      datasets: props.series.map((slice) => {
        const hue = css(slice.color)
        return {
          label: slice.label,
          data: [slice.values[0] ?? 0],
          backgroundColor: hue,
          hoverBackgroundColor: rgba(hue, 0.82),
          // The surface shows between the pieces, as it does between columns.
          borderColor: css('--c-surface'),
          borderWidth: 2,
          borderSkipped: false,
          borderRadius: 4,
          barThickness: 44,
        }
      }),
    },
    options: {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 220 },
      layout: { padding: { top: 8, bottom: 8 } },
      interaction: { mode: 'nearest', intersect: true },
      scales: {
        x: {
          stacked: true,
          display: !props.masked,
          border: { display: false },
          grid: { drawTicks: false, lineWidth: 1, color: css('--c-line-soft') },
          ticks: {
            color: css('--c-faint'),
            font: { size: 11 },
            maxTicksLimit: 6,
            callback: (value) => (props.tickFormat ?? props.format)(value),
          },
        },
        y: { stacked: true, display: false },
      },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false, external: externalTooltip },
        segmentLabels: {
          shares: props.series.map((slice) =>
            splitTotal.value > 0 ? (slice.values[0] ?? 0) / splitTotal.value : 0,
          ),
          names: props.series.map((slice) => slice.label),
          color: css('--c-text'),
          halo: css('--c-surface'),
          masked: props.masked,
        },
      },
    },
    plugins: [segmentLabels],
  }
}

function config() {
  if (isPie.value) return pieConfig()
  if (isSegments.value) return segmentsConfig()
  const line = props.type === 'line'
  return {
    type: props.type,
    data: {
      labels: props.labels,
      datasets: props.series.map((series, index) => {
        const hue = css(series.color)
        const last = series.values.length - 1
        const asLine = isLine(series)
        // An overlaid tag line is drawn on top of the columns it annotates.
        const overlay = asLine && !line
        return {
          type: series.type ?? props.type,
          label: series.label,
          data: series.values,
          order: overlay ? 0 : index + 1,
          borderColor: hue,
          borderDash: series.dash ?? [],
          backgroundColor: asLine ? rgba(hue, overlay ? 0 : 0.1) : hue,
          hoverBackgroundColor: asLine ? rgba(hue, overlay ? 0 : 0.1) : rgba(hue, 0.82),
          // Only the base line wears the wash: a stack of filled areas would
          // paint over each other. The wash meets the axis, not an off-screen zero.
          fill: series.fill === true && asLine && !overlay ? 'start' : false,
          borderWidth: asLine ? 2 : 0,
          tension: 0,
          pointRadius: (ctx) =>
            series.fill === true && asLine && !overlay && ctx.dataIndex === last ? 4.5 : 0,
          pointHoverRadius: 4.5,
          pointBackgroundColor: hue,
          pointBorderColor: css('--c-surface'),
          pointBorderWidth: 2,
          borderRadius: line ? 0 : { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 },
          borderSkipped: 'bottom',
          maxBarThickness: 22,
          categoryPercentage: 0.7,
          barPercentage: 0.94, // the 2px surface gap between paired columns
        }
      }),
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 220 },
      layout: { padding: { top: 20 } },
      interaction: { mode: 'index', intersect: false },
      scales: {
        x: {
          grid: { display: false },
          border: { color: css('--c-line'), width: 1 },
          ticks: {
            color: css('--c-faint'),
            font: { size: 11 },
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 7,
          },
        },
        y: {
          display: !props.masked,
          min: scale.value.min,
          max: scale.value.max,
          border: { display: false },
          grid: {
            drawTicks: false,
            lineWidth: 1,
            color: (ctx) => (ctx.tick.value === 0 ? css('--c-line') : css('--c-line-soft')),
          },
          ticks: {
            color: css('--c-faint'),
            font: { size: 11 },
            stepSize: scale.value.step,
            padding: 6,
            callback: (value) => (props.tickFormat ?? props.format)(value),
          },
        },
      },
      plugins: {
        legend: { display: false }, // drawn in the DOM below, where it can be styled
        tooltip: { enabled: false, external: externalTooltip },
        directLabel: {
          index: labelAt.value,
          text: props.format(props.series[0]?.values[labelAt.value] ?? 0),
          color: css('--c-text'),
          halo: css('--c-surface'),
        },
        boundaries: { marks: props.marks, line: css('--c-line'), text: css('--c-faint') },
      },
    },
    plugins: [directLabel, boundaries],
  }
}

/**
 * Dragging a span out of the plot, the way a network panel does it: press,
 * sweep, release, and the ends become a range. Mouse and pen only — on a touch
 * screen the same gesture is how you scroll the card, so the picker is the way
 * in there instead.
 */
const inArea = (x) => {
  const area = chart?.chartArea
  return area ? Math.min(Math.max(x, area.left), area.right) : x
}
const indexAt = (x) =>
  Math.min(Math.max(Math.round(chart.scales.x.getValueForPixel(x)), 0), props.labels.length - 1)

/** Where the band sits, and what the two ends are called. */
const band = computed(() => {
  if (!drag.value?.moved) return null
  const [a, b] = [drag.value.start, drag.value.end].sort((x, y) => x - y)
  const ends = [indexAt(a), indexAt(b)]
  return {
    left: a,
    width: Math.max(1, b - a),
    label: ends[0] === ends[1] ? props.labels[ends[0]] : `${props.labels[ends[0]]} — ${props.labels[ends[1]]}`,
  }
})

function onPointerDown(event) {
  if (!props.selectable || !chart || event.pointerType === 'touch' || event.button !== 0) return
  const area = chart.chartArea
  if (event.offsetX < area.left - 6 || event.offsetX > area.right + 6) return
  drag.value = { start: inArea(event.offsetX), end: inArea(event.offsetX), moved: false }
  tip.value = null
  canvas.value.setPointerCapture(event.pointerId)
}

function onPointerMove(event) {
  if (!drag.value) return
  drag.value.end = inArea(event.offsetX)
  // A few pixels of slop, so a click that wobbles is still a click.
  if (Math.abs(drag.value.end - drag.value.start) > 4) drag.value.moved = true
}

function onPointerUp() {
  const span = drag.value
  drag.value = null
  if (!span?.moved || !chart) return
  const [a, b] = [span.start, span.end].sort((x, y) => x - y)
  emit('select', { from: indexAt(a), to: indexAt(b) })
}

const cancelDrag = () => (drag.value = null)
const onKey = (event) => event.key === 'Escape' && cancelDrag()

function draw() {
  if (!canvas.value) return
  chart?.destroy()
  tip.value = null
  chart = new Chart(canvas.value, config())
}

onMounted(() => {
  draw()
  window.addEventListener('keydown', onKey)
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKey)
  chart?.destroy()
  chart = null
})

// Colours are resolved against the live theme, so a theme flip redraws too.
watch(
  () => [props.labels, props.series, props.masked, props.type, state.settings.theme],
  draw,
  { deep: true },
)
</script>

<template>
  <!-- `relative` is load-bearing: `sr-only` positions the readable table
       absolutely, and with no positioned ancestor its containing block is the
       page itself — so it escapes the charts' horizontal scroller and drags the
       document sideways on a phone. -->
  <div class="relative">
    <!-- Identity never rests on colour alone: two or more series get a legend. -->
    <ul v-if="showLegend" class="mb-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-muted">
      <li v-for="s in series" :key="s.label" class="flex items-center gap-1.5">
        <span
          v-if="type !== 'pie' && type !== 'segments' && (s.type ?? type) === 'line'"
          class="h-0.5 w-3.5 rounded-full"
          :style="{
            background:
              s.dash && s.dash.length
                ? `repeating-linear-gradient(90deg, var(${s.color}) 0 ${s.dash[0]}px, transparent ${s.dash[0]}px ${s.dash[0] + s.dash[1]}px)`
                : `var(${s.color})`,
          }"
        />
        <span v-else class="size-2 rounded-[2px]" :style="{ background: `var(${s.color})` }" />
        {{ s.label }}
      </li>
    </ul>

    <div class="relative" :style="{ height: `${height}px` }">
      <!-- Chart.js sizes the canvas itself, one frame behind a resize; the cap
           stops that frame from widening the page on a phone. -->
      <canvas
        ref="canvas"
        class="max-w-full"
        :class="selectable && 'cursor-crosshair'"
        role="img"
        :aria-label="caption"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="cancelDrag"
      />

      <!-- What the slices add up to, in the space they leave. -->
      <div
        v-if="centre && type === 'pie'"
        class="pointer-events-none absolute inset-0 grid place-content-center text-center"
      >
        <p class="tnum text-[15px] leading-tight font-bold">{{ centre.value }}</p>
        <p class="text-[11px] text-faint">{{ centre.label }}</p>
      </div>

      <!-- The band under the pointer, named as it is swept out. -->
      <div
        v-if="band"
        class="pointer-events-none absolute inset-y-0 z-10 border-x border-accent/60 bg-accent/12"
        :style="{ left: `${band.left}px`, width: `${band.width}px` }"
      >
        <span
          class="absolute -top-1 left-1/2 -translate-x-1/2 rounded-md border border-line bg-surface
                 px-1.5 py-0.5 text-[11px] font-semibold whitespace-nowrap text-muted"
        >
          {{ band.label }}
        </span>
      </div>

      <div
        v-if="tip"
        class="pointer-events-none absolute -top-1 z-10 -translate-x-1/2 rounded-lg border border-line
               bg-surface px-2 py-1 shadow-[var(--c-shadow)]"
        :style="{ left: `${tip.x}px` }"
      >
        <p v-if="tip.label" class="mb-0.5 text-center text-[11px] text-muted">{{ tip.label }}</p>
        <p
          v-for="row in tip.rows"
          :key="row.name"
          class="flex items-center justify-center gap-1.5 text-[12.5px] font-semibold whitespace-nowrap"
        >
          <span class="h-0.5 w-2.5 rounded-full" :style="{ background: row.color }" />
          <span v-if="type === 'pie' || type === 'segments'" class="font-medium text-muted">
            {{ row.name }}
          </span>
          <span class="tnum">{{ row.value }}</span>
        </p>
      </div>
    </div>

    <p v-if="masked" class="sr-only">{{ t('charts.maskedNote', { caption }) }}</p>
    <!-- The wrapper does the hiding: `sr-only` on a table is not enough, a table
         grows to its content whatever width it is given, and would then push the
         page sideways on a phone. -->
    <div v-else-if="type === 'pie' || type === 'segments'" class="sr-only">
      <table>
        <caption>{{ caption }}</caption>
        <tbody>
          <tr v-for="s in series" :key="s.label">
            <th scope="row">{{ s.label }}</th>
            <td>{{ format(s.values[0]) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else-if="type === 'bar'" class="sr-only">
      <table>
        <caption>{{ caption }}</caption>
        <thead>
          <tr>
            <th></th>
            <th v-for="s in series" :key="s.label">{{ s.label }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(label, i) in labels" :key="label">
            <th scope="row">{{ label }}</th>
            <td v-for="s in series" :key="s.label">{{ format(s.values[i]) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
